/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        AdjustCreditsModal Component (Task 11.4.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Coins,
  PlusCircle,
  MinusCircle,
  X,
  AlertTriangle,
  ArrowRight,
  Loader2,
  Mail,
  ShieldAlert,
} from 'lucide-react';
import { useI18n } from '../i18n';
import { adminClient } from '../lib/api-client';
import type {
  AdminCreditAdjustmentResponse,
  AdminCreditAdjustmentInput,
} from '@sofia/shared-types';

export interface AdjustCreditsModalProps {
  workspaceId: string;
  workspaceName: string;
  currentBalance: number;
  userEmail?: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (result: AdminCreditAdjustmentResponse) => void;
}

export const AdjustCreditsModal: React.FC<AdjustCreditsModalProps> = ({
  workspaceId,
  workspaceName,
  currentBalance,
  userEmail,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useI18n();

  const [mode, setMode] = useState<'add' | 'deduct'>('add');
  const [amountStr, setAmountStr] = useState<string>('5000');
  const [reason, setReason] = useState<string>('');
  const [notifyUser, setNotifyUser] = useState<boolean>(false);
  const [clientError, setClientError] = useState<string | null>(null);

  const amountNum = Math.abs(parseFloat(amountStr) || 0);
  const effectiveDiff = mode === 'add' ? amountNum : -amountNum;
  const predictedBalance = Math.round((currentBalance + effectiveDiff) * 10000) / 10000;
  const isNegative = predictedBalance < 0;

  const mutation = useMutation<
    { success: boolean; data: AdminCreditAdjustmentResponse },
    Error,
    AdminCreditAdjustmentInput
  >({
    mutationFn: async (payload) => {
      return adminClient.post(`/api/v1/admin/workspaces/${workspaceId}/adjust-credits`, payload);
    },
    onSuccess: (data) => {
      onSuccess(data.data);
      onClose();
    },
    onError: (err) => {
      setClientError(err.message);
    },
  });

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setClientError(null);

    if (amountNum <= 0) {
      setClientError(t('admin.users.validationAmountZero'));
      return;
    }

    if (reason.trim().length < 3) {
      setClientError(t('admin.users.validationReasonMin'));
      return;
    }

    if (mode === 'deduct' && isNegative) {
      setClientError(t('admin.users.insufficientCreditsNotice'));
      return;
    }

    mutation.mutate({
      amount: effectiveDiff,
      reason: reason.trim(),
      notifyUser,
    });
  };

  const quickPresets = mode === 'add'
    ? [1000, 5000, 10000, 50000]
    : [500, 1000, 2500, 5000];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/65 backdrop-blur-sm p-4 sm:p-6 animate-in fade-in duration-150">
      <div className="w-full max-w-lg rounded-3xl bg-surface border border-surface-border shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-border bg-surface-elevated shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-500">
              <Coins className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-heading font-bold text-foreground">
                {t('admin.users.adjustCreditsTitle')}
              </h3>
              <p className="text-[11px] text-muted-fg font-mono">
                {workspaceName} (#{workspaceId.slice(0, 8)}) {userEmail ? `• ${userEmail}` : ''}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            className="p-1.5 rounded-xl text-muted-fg hover:text-foreground hover:bg-surface transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5 overflow-y-auto max-h-[75vh]">
          {/* Notice banner */}
          <p className="text-xs text-muted-fg leading-relaxed">
            {t('admin.users.adjustCreditsDesc')}
          </p>

          {/* Mode Switcher */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground">
              {t('admin.users.adjustMode')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode('add')}
                className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold border transition-all ${
                  mode === 'add'
                    ? 'bg-emerald-500/10 border-emerald-500 text-emerald-500 shadow-sm'
                    : 'bg-surface-elevated border-surface-border text-muted-fg hover:text-foreground'
                }`}
              >
                <PlusCircle className="w-4 h-4" />
                <span>{t('admin.users.adjustModeAdd')}</span>
              </button>

              <button
                type="button"
                onClick={() => setMode('deduct')}
                className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold border transition-all ${
                  mode === 'deduct'
                    ? 'bg-rose-500/10 border-rose-500 text-rose-500 shadow-sm'
                    : 'bg-surface-elevated border-surface-border text-muted-fg hover:text-foreground'
                }`}
              >
                <MinusCircle className="w-4 h-4" />
                <span>{t('admin.users.adjustModeDeduct')}</span>
              </button>
            </div>
          </div>

          {/* Amount input & Quick presets */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-foreground">
                {t('admin.users.adjustAmount')}
              </label>
              <span className="text-[11px] text-muted-fg">
                {t('admin.users.currentBalance')}:{' '}
                <strong className="text-foreground font-mono">{currentBalance.toLocaleString()}</strong>
              </span>
            </div>

            <div className="relative">
              <input
                type="number"
                min="1"
                step="any"
                value={amountStr}
                onChange={(e) => setAmountStr(e.target.value)}
                placeholder={t('admin.users.adjustAmountPlaceholder')}
                className="w-full px-4 py-2.5 rounded-xl bg-surface-elevated border border-surface-border text-foreground text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-mono"
              />
              <div className="absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-semibold text-muted-fg pointer-events-none">
                Credits
              </div>
            </div>

            {/* Presets */}
            <div className="flex items-center gap-1.5 pt-1">
              {quickPresets.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setAmountStr(preset.toString())}
                  className="px-2.5 py-1 rounded-lg text-[11px] font-mono font-medium bg-surface-elevated hover:bg-surface border border-surface-border text-muted-fg hover:text-foreground transition-colors"
                >
                  {mode === 'add' ? `+${preset.toLocaleString()}` : `-${preset.toLocaleString()}`}
                </button>
              ))}
            </div>
          </div>

          {/* Balance projection Card */}
          <div
            className={`p-4 rounded-2xl border transition-all ${
              isNegative
                ? 'bg-rose-500/10 border-rose-500/30'
                : 'bg-surface-elevated border-surface-border'
            }`}
          >
            <div className="flex items-center justify-between text-xs">
              <span className="font-semibold text-muted-fg">
                {t('admin.users.predictedBalance')}
              </span>
              {isNegative && (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-500">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  <span>Negative Balance</span>
                </span>
              )}
            </div>

            <div className="flex items-center gap-3 mt-2">
              <div className="flex items-center gap-1.5 font-mono text-xs text-muted-fg">
                <span>{currentBalance.toLocaleString()}</span>
              </div>
              <ArrowRight className="w-3.5 h-3.5 text-muted-fg shrink-0" />
              <div className="flex items-center gap-1 font-mono text-xs font-semibold">
                <span className={mode === 'add' ? 'text-emerald-500' : 'text-rose-500'}>
                  {mode === 'add' ? `+${amountNum.toLocaleString()}` : `-${amountNum.toLocaleString()}`}
                </span>
              </div>
              <span className="text-muted-fg font-mono">=</span>
              <div
                className={`flex items-center gap-1 text-base font-mono font-bold ${
                  isNegative ? 'text-rose-500' : 'text-foreground'
                }`}
              >
                <Coins className="w-4 h-4 text-amber-500" />
                <span>{predictedBalance.toLocaleString()}</span>
              </div>
            </div>

            {isNegative && (
              <p className="text-[11px] text-rose-500 mt-2 font-medium">
                {t('admin.users.insufficientCreditsNotice')}
              </p>
            )}
          </div>

          {/* Audit Reason Textarea */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground flex items-center justify-between">
              <span>{t('admin.users.adjustReason')}</span>
              <span className="text-[10px] text-rose-500 font-normal">*Required</span>
            </label>
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('admin.users.adjustReasonPlaceholder')}
              className="w-full px-3.5 py-2.5 rounded-xl bg-surface-elevated border border-surface-border text-foreground text-xs placeholder:text-muted-fg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
            />
          </div>

          {/* Notify User Checkbox */}
          <label className="flex items-start gap-2.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={notifyUser}
              onChange={(e) => setNotifyUser(e.target.checked)}
              className="mt-0.5 rounded border-surface-border text-primary focus:ring-primary/20 bg-surface-elevated"
            />
            <div className="text-xs">
              <span className="font-medium text-foreground flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5 text-primary" />
                {t('admin.users.notifyUserLabel')}
              </span>
            </div>
          </label>

          {/* Error Banner */}
          {clientError && (
            <div className="flex items-start gap-2 p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-xs text-rose-500">
              <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{clientError}</span>
            </div>
          )}

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-surface-border">
            <button
              type="button"
              onClick={onClose}
              disabled={mutation.isPending}
              className="px-4 py-2 rounded-xl text-xs font-semibold text-muted-fg hover:text-foreground hover:bg-surface-elevated border border-surface-border transition-colors disabled:opacity-50"
            >
              {t('common.cancel')}
            </button>

            <button
              type="submit"
              disabled={
                mutation.isPending ||
                amountNum <= 0 ||
                reason.trim().length < 3 ||
                (mode === 'deduct' && isNegative)
              }
              className={`inline-flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-semibold text-white transition-all shadow-md ${
                mode === 'add'
                  ? 'bg-emerald-600 hover:bg-emerald-500'
                  : 'bg-rose-600 hover:bg-rose-500'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {mutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{t('admin.users.adjustingBtn')}</span>
                </>
              ) : (
                <>
                  <Coins className="w-4 h-4" />
                  <span>{t('admin.users.confirmAdjustBtn')}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
